import { observer } from "mobx-react-lite";
import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSModalDialog,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSText,
  MDSTextVariant,
  MDSDialogChip,
  MDSDialogChipVariant,
} from "@/mds";
import { DeleteSharedNotesModalStore } from "@/components/modal/delete-shared-notes/delete-shared-notes-modal-store";
import { INoteObservable } from "@/store/note";
import { pluralize } from "@/modules/pluralize";
import { useModalTitleLimit } from "@/components/modal/modal-title-limits";
import styled from "@emotion/styled";

export interface DeleteSharedNotesModalProps {
  store: DeleteSharedNotesModalStore;
}

export const DeleteSharedNotesModal = observer<DeleteSharedNotesModalProps>(({ store }) => {
  const primaryTitle = store.notes[0]?.title || "";
  const secondaryTitle = getSecondaryTitle(store.notes);
  const isAllShared = store.notes.every(note => note.isShared);
  const prefix = getTitlePrefix({
    isAllShared,
    noteCount: store.notes.length,
  });
  const title = useModalTitleLimit({ primaryTitle, secondaryTitle, isAllShared });

  if (!store.notes.length) return null;

  return (
    <MDSModalDialog isDismissable isOpen={store.isOpen} handleClose={() => store.close()}>
      <Title slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
        <TitlePrefix>{prefix}</TitlePrefix>
        <MDSDialogChip inline label={title} variant={MDSDialogChipVariant.Note} />
        {secondaryTitle && <TitleSecondary>{secondaryTitle}</TitleSecondary>}
        <TitlePunctuation>?</TitlePunctuation>
      </Title>

      {store.notes.length === 1 && (
        <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
          <MDSText variant={MDSTextVariant.ParagraphS}>
            If you delete this note, collaborators will still be able to access it for up to 30 days
            or until you clear the Trash.
          </MDSText>
          <MDSText variant={MDSTextVariant.ParagraphS}>
            Once permanently deleted, nobody will be able to access it.
          </MDSText>
        </MDSDialogTextContent>
      )}
      {store.notes.length > 1 && (
        <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
          {!isAllShared && (
            <MDSText variant={MDSTextVariant.ParagraphS}>
              Some notes you selected were shared with others.
            </MDSText>
          )}
          <MDSText variant={MDSTextVariant.ParagraphS}>
            Once notes are moved to trash, collaborators on shared notes will still be able to
            access it for 30 days or until you clear the trash.
          </MDSText>
          <MDSText variant={MDSTextVariant.ParagraphS}>
            Once permanently deleted, nobody will be able to access them.
          </MDSText>
        </MDSDialogTextContent>
      )}

      <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
        <MDSButton onClick={() => store.close()}>Cancel</MDSButton>
        <MDSButton variant={MDSButtonVariant.DangerFilled} onClick={() => store.handleSubmit()}>
          Delete {pluralize(store.notes.length, "note", "notes")}
        </MDSButton>
      </MDSButtonGroup>
    </MDSModalDialog>
  );
});

const getSecondaryTitle = (items: INoteObservable[]) => {
  if (items.length === 1) return "";
  return `and ${items.length - 1} other ${pluralize(items.length - 1, "note", "notes")}`;
};

const getTitlePrefix = ({
  isAllShared,
  noteCount,
}: {
  isAllShared: boolean;
  noteCount: number;
}) => {
  if (noteCount === 1) {
    return "Delete shared note";
  }

  if (isAllShared) {
    return "Delete shared";
  }

  return "Delete";
};

const Title = styled(MDSText)`
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  flex-wrap: wrap;
  /* Flex column-gap only applies between items on the same line, so wrapped chips stay flush-left. */
  column-gap: ${({ theme }) => theme.spacing["1"]};
  row-gap: 0;
  min-width: 0;

  > [data-mds-name="dialog-chip"] {
    flex: 1 1 0;
    max-width: max-content;
    min-width: 0;
  }
`;

const TitlePrefix = styled.span`
  flex: 0 0 auto;
  white-space: nowrap;
`;

const TitleSecondary = styled.span`
  flex: 0 0 auto;
  white-space: nowrap;
`;

const TitlePunctuation = styled.span`
  flex: 0 0 auto;
  white-space: nowrap;
`;
